import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'localization';
// eslint-disable-next-line no-restricted-imports
import type { DataTableSortEvent } from 'primereact/datatable';
import type { Context } from 'types/context';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SearchBarAtom from '../../atoms/SearchBar/SearchBarAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl/SegmentedControlAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import { DataListMolecule } from '../../molecules/DataList';
import ContextCardMolecule from '../../molecules/ContextCard/ContextCardMolecule';
import type { DataGridColumn } from '../../atoms/DataGrid/DataGridAtom';
import ContextSummaryMolecule from '../../molecules/ContextSummary/ContextSummaryMolecule';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import {
  classifyContextSource,
  resolveLocalizedText,
  getAppliesToTypes,
} from '../../../utils/contextClassification';
import { useContextLibrary } from '../../../hooks/useContextLibrary';
import './ContextInventoryOrganism.scss';

const LAYOUT_STORAGE_KEY = 'context-library-layout';
type LayoutMode = 'grid' | 'table';

function readStoredLayout(): LayoutMode {
  try {
    const stored = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (stored === 'table') return 'table';
  } catch {
    // ignore
  }
  return 'grid';
}

export interface ContextInventoryOrganismProps {
  contexts: Context[];
  loading?: boolean;
  onNew?: () => void;
  onEdit?: (context: Context) => void;
  onClone?: (context: Context) => void;
  onDelete?: (context: Context) => void;
  onRefresh?: () => void;
  locale?: string;
  className?: string;
}

const ContextInventoryOrganism: React.FC<ContextInventoryOrganismProps> = ({
  contexts,
  loading = false,
  onNew,
  onEdit,
  onClone,
  onDelete,
  onRefresh,
  locale: localeProp,
  className = '',
}) => {
  const { t, i18n } = useTranslation();
  const locale = localeProp || i18n.language || 'en-US';

  const [layout, setLayout] = React.useState<LayoutMode>(readStoredLayout);
  const [sortField, setSortField] = React.useState<string>('updated_at');
  const [sortOrder, setSortOrder] = React.useState<1 | -1>(-1);

  const { filteredContexts, useCases, filters, setFilters, stats } = useContextLibrary(
    contexts,
    locale
  );

  const handleLayoutChange = useCallback((value: string | string[]) => {
    const mode = value as LayoutMode;
    setLayout(mode);
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }, []);

  const handleSort = useCallback((e: DataTableSortEvent) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder as 1 | -1);
  }, []);

  const handleSourceChange = useCallback((value: string | string[]) => {
    setFilters({ ...filters, source: value as 'all' | 'platform' | 'org' });
  }, [filters, setFilters]);

  const handleSearchChange = useCallback((value: string) => {
    setFilters({ ...filters, search: value });
  }, [filters, setFilters]);

  const handleUseCaseToggle = useCallback((tag: string) => {
    const current = filters.useCaseTags ?? [];
    const next = current.includes(tag)
      ? current.filter((t) => t !== tag)
      : [...current, tag];
    setFilters({ ...filters, useCaseTags: next });
  }, [filters, setFilters]);

  const hasActiveFilters =
    filters.source !== 'all' ||
    (filters.search ?? '') !== '' ||
    (filters.useCaseTags ?? []).length > 0;

  const handleClearFilters = useCallback(() => {
    setFilters({ source: 'all', search: '', useCaseTags: [] });
  }, [setFilters]);

  const layoutOptions = useMemo(() => [
    { value: 'grid', label: t('context.library.gridToggle'), icon: 'pi pi-th-large' },
    { value: 'table', label: t('context.library.tableToggle'), icon: 'pi pi-list' },
  ], [t]);

  const sourceOptions = useMemo(() => [
    { value: 'all', label: t('context.library.filterSource.all') },
    { value: 'org', label: t('context.library.filterSource.yours') },
    { value: 'platform', label: t('context.library.filterSource.platform') },
    { value: 'approach', label: t('context.library.filterSource.approach'), disabled: true },
  ], [t]);

  // Column body renderers for table mode
  const nameBody = useCallback((rowData: Context) => {
    const builtIn = classifyContextSource(rowData) === 'platform';
    return (
      <span data-testid={`context-name-${rowData.system_name}`} className="context-inventory-organism__name-cell">
        {resolveLocalizedText(rowData.name, locale)}
        {builtIn && (
          <span className="context-inventory-organism__built-in-badge" title={t('context.inventory.builtIn')}>
            <i className="pi pi-lock" />
          </span>
        )}
      </span>
    );
  }, [locale, t]);

  const levelsBody = useCallback((rowData: Context) => (
    <span>{rowData.definition?.levels?.length ?? 0}</span>
  ), []);

  const descriptionBody = useCallback((rowData: Context) => {
    const desc = resolveLocalizedText(rowData.description, locale);
    if (!desc) return <span className="context-inventory-organism__muted">—</span>;
    return <span className="truncate-cell" title={desc}>{desc}</span>;
  }, [locale]);

  const dateBody = useCallback((dateStr: string | undefined) => {
    if (!dateStr) return <span>—</span>;
    return <span>{new Date(dateStr).toLocaleDateString(locale)}</span>;
  }, [locale]);

  const createdAtBody = useCallback((rowData: Context) => dateBody(rowData.created_at), [dateBody]);
  const updatedAtBody = useCallback((rowData: Context) => dateBody(rowData.updated_at), [dateBody]);

  const walkShapeBody = useCallback((rowData: Context) => (
    <ContextSummaryMolecule
      context={{
        systemName: rowData.system_name,
        name: resolveLocalizedText(rowData.name, locale),
        definition: rowData.definition,
        applicability: rowData.applicability,
      }}
      mode="inline"
    />
  ), [locale]);

  const sourceBody = useCallback((rowData: Context) => {
    const src = classifyContextSource(rowData);
    const label = src === 'platform'
      ? t('context.library.card.sourcePlatform')
      : t('context.library.card.sourceYours');
    return (
      <StatusBadgeAtom
        tone="info"
        label={label}
        icon={src === 'platform' ? 'pi pi-lock' : undefined}
      />
    );
  }, [t]);

  const useCaseBody = useCallback((rowData: Context) => {
    const tags = rowData.use_case_tags ?? [];
    if (!tags.length) return <span className="context-inventory-organism__muted">—</span>;
    return (
      <div className="context-inventory-organism__tag-list">
        {tags.map((tag) => {
          const uc = useCases.find((u) => u.system_name === tag);
          return <TagAtom key={tag} label={uc?.name ?? tag} size="small" tone="accent" />;
        })}
      </div>
    );
  }, [useCases]);

  const viewsBody = useCallback((rowData: Context) => (
    <span>{rowData.viewCount ?? 0}</span>
  ), []);

  const actionsBody = useCallback((rowData: Context) => {
    const builtIn = classifyContextSource(rowData) === 'platform';
    return (
      <div className="context-inventory-organism__actions">
        <ButtonAtom
          variant="text"
          size="small"
          icon={builtIn ? 'pi pi-eye' : 'pi pi-pencil'}
          onClick={() => onEdit?.(rowData)}
          tooltip={builtIn ? t('context.inventory.view') : t('context.inventory.edit')}
          tooltipOptions={{ position: 'top' }}
          aria-label={builtIn ? t('context.inventory.view') : t('context.inventory.edit')}
          data-testid={`edit-${rowData.system_name}`}
        />
        <ButtonAtom
          variant="text"
          size="small"
          icon="pi pi-copy"
          onClick={() => onClone?.(rowData)}
          tooltip={t('context.inventory.clone')}
          tooltipOptions={{ position: 'top' }}
          aria-label={t('context.inventory.clone')}
          data-testid={`clone-${rowData.system_name}`}
        />
        <ButtonAtom
          variant="text"
          size="small"
          icon="pi pi-trash"
          className="p-button-danger"
          onClick={() => onDelete?.(rowData)}
          tooltip={builtIn ? t('context.inventory.builtInReadOnly') : t('context.inventory.delete')}
          tooltipOptions={{ position: 'top' }}
          aria-label={t('context.inventory.delete')}
          data-testid={`delete-${rowData.system_name}`}
          disabled={builtIn}
        />
      </div>
    );
  }, [onEdit, onClone, onDelete, t]);

  const columns: DataGridColumn[] = useMemo(() => [
    { field: 'name', header: t('context.inventory.columns.name'), body: nameBody, sortable: true, sortField: 'name' },
    { field: 'description', header: t('context.inventory.columns.description'), body: descriptionBody, minWidth: '200px' },
    { field: 'levels', header: t('context.inventory.columns.levels'), body: levelsBody, sortable: true, width: '80px' },
    { field: 'walkShape', header: t('context.library.columns.walkShape'), body: walkShapeBody, width: '480px', minWidth: '480px' },
    { field: 'source', header: t('context.library.columns.source'), body: sourceBody, width: '110px' },
    { field: 'useCase', header: t('context.library.columns.useCase'), body: useCaseBody },
    { field: 'views', header: t('context.library.columns.views'), body: viewsBody, sortable: false, width: '80px' },
    { field: 'created_by', header: t('context.inventory.columns.createdBy'), isSphereId: true, width: '150px' },
    { field: 'updated_at', header: t('context.inventory.columns.updatedAt'), body: updatedAtBody, sortable: true, width: '100px' },
    { field: 'actions', header: t('context.inventory.columns.actions'), body: actionsBody, width: '120px' },
  ], [t, nameBody, descriptionBody, levelsBody, walkShapeBody, sourceBody, useCaseBody, viewsBody, updatedAtBody, actionsBody]);

  const mobileCardTemplate = useCallback((ctx: Context) => {
    const builtIn = classifyContextSource(ctx) === 'platform';
    return (
      <div className="data-list-mobile-card">
        <div className="mobile-card-header">
          <span className="context-inventory-organism__name-cell">
            {resolveLocalizedText(ctx.name, locale)}
            {builtIn && (
              <span className="context-inventory-organism__built-in-badge">
                <i className="pi pi-lock" />
              </span>
            )}
          </span>
        </div>
        <div className="mobile-card-footer">
          <div className="context-inventory-organism__actions">
            <ButtonAtom
              variant="text"
              size="small"
              icon={builtIn ? 'pi pi-eye' : 'pi pi-pencil'}
              onClick={() => onEdit?.(ctx)}
              aria-label={builtIn ? t('context.inventory.view') : t('context.inventory.edit')}
            />
            <ButtonAtom
              variant="text"
              size="small"
              icon="pi pi-copy"
              onClick={() => onClone?.(ctx)}
              aria-label={t('context.inventory.clone')}
            />
            <ButtonAtom
              variant="text"
              size="small"
              icon="pi pi-trash"
              className="p-button-danger"
              onClick={() => onDelete?.(ctx)}
              aria-label={t('context.inventory.delete')}
              disabled={builtIn}
            />
          </div>
        </div>
      </div>
    );
  }, [locale, onEdit, onClone, onDelete, t]);

  const noResultsState: React.ReactNode = hasActiveFilters ? (
    <div className="context-inventory-organism__gallery-empty">
      <EmptyStateAtom
        icon="pi-search"
        title={t('context.library.emptyNoMatch')}
        description={t('context.library.emptyNoMatchDesc')}
      />
      <ButtonAtom
        variant="secondary"
        label={t('context.library.clearFilters')}
        icon="pi pi-times"
        onClick={handleClearFilters}
        data-testid="clear-filters-btn"
      />
    </div>
  ) : (
    <div className="context-inventory-organism__gallery-empty">
      <EmptyStateAtom
        icon="pi-inbox"
        title={t('context.library.emptyNoContexts')}
        description={t('context.library.emptyNoContextsDesc')}
      />
    </div>
  );

  const galleryContent: React.ReactNode = filteredContexts.length > 0 ? (
    filteredContexts.map((ctx) => {
      const source = classifyContextSource(ctx);
      const appliesTo = getAppliesToTypes(ctx.applicability);
      return (
        <ContextCardMolecule
          key={ctx.system_name}
          context={ctx}
          source={source}
          appliesTo={appliesTo}
          useCases={useCases}
          locale={locale}
          onEdit={onEdit}
          onClone={onClone}
          onDelete={onDelete}
        />
      );
    })
  ) : noResultsState;

  const newContextButton = (
    <ButtonAtom
      variant="primary"
      label={t('context.inventory.newContext')}
      icon="pi pi-plus"
      onClick={onNew}
      className="context-inventory-organism__new-btn"
      data-testid="new-context-button"
    />
  );

  return (
    <div className={`context-inventory-organism ${className}`} data-testid="context-inventory">
      {/* Page header — title and summary stats */}
      <div className="context-inventory-organism__header">
        <div className="context-inventory-organism__header-label">
          {t('context.library.organizationLibrary') || 'ORGANIZATION LIBRARY'}
        </div>
        <div className="context-inventory-organism__title-stats-row">
          <h1 className="context-inventory-organism__title">
            {t('context.library.title') || 'Contexts'}
          </h1>
          <span className="context-inventory-organism__stats">
            {t('context.library.stats.contexts', { count: stats.total })}
            {' · '}
            {t('context.library.stats.platform', { count: stats.platformCount })}
            {' · '}
            {t('context.library.stats.yours', { count: stats.orgCount })}
            {' · '}
            {t('context.library.stats.views', { count: stats.totalViews })}
          </span>
          {newContextButton}
        </div>
        <p className="context-inventory-organism__description">
          {t('context.library.description') || 'Saved hierarchical queries for organizing and exploring your data'}
        </p>
      </div>

      {/* Filter rail */}
      <div className="context-inventory-organism__filter-rail">
        <div className="context-inventory-organism__filter-row-1">
          <SearchBarAtom
            value={filters.search ?? ''}
            onChange={handleSearchChange}
            placeholder={t('context.library.searchPlaceholder') || 'Search by name, walk shape, or use case'}
            className="context-inventory-organism__search-bar"
          />
          <SegmentedControlAtom
            options={sourceOptions}
            value={filters.source ?? 'all'}
            onChange={handleSourceChange}
            mode="single"
            size="small"
            data-testid="source-filter"
          />
          <SegmentedControlAtom
            options={layoutOptions}
            value={layout}
            onChange={handleLayoutChange}
            mode="single"
            variant="strip"
            size="small"
            data-testid="layout-toggle"
          />
        </div>
        {useCases.length > 0 && (
          <div className="context-inventory-organism__filter-row-2">
            <span className="context-inventory-organism__use-case-label">
              {t('context.library.useCaseLabel') || 'USE CASE'}
            </span>
            <div className="context-inventory-organism__use-case-chips">
              {useCases.map((uc) => (
                <TagAtom
                  key={uc.system_name}
                  label={uc.name}
                  icon={uc.icon || undefined}
                  size="small"
                  tone="neutral"
                  pressed={(filters.useCaseTags ?? []).includes(uc.system_name)}
                  onClick={() => handleUseCaseToggle(uc.system_name)}
                  data-testid={`use-case-chip-${uc.system_name}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      {layout === 'grid' ? (
        <div className="context-inventory-organism__gallery" data-testid="context-gallery">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="context-inventory-organism__skeleton-card"
                aria-hidden="true"
              />
            ))
          ) : galleryContent}
          {!loading && (
            <button
              type="button"
              className="context-inventory-organism__new-tile"
              onClick={onNew}
              data-testid="new-context-tile"
            >
              <i className="pi pi-plus" aria-hidden="true" />
              <span>{t('context.library.newContextTile')}</span>
            </button>
          )}
        </div>
      ) : (
        <DataListMolecule
          data={filteredContexts}
          columns={columns}
          loading={loading}
          emptyMessage={hasActiveFilters ? t('context.library.emptyNoMatch') : t('context.inventory.empty')}
          dataKey="id"
          title={t('context.inventory.title')}
          icon="pi pi-sitemap"
          collapsible={false}
          showSearch={false}
          showRefresh={!!onRefresh}
          onRefresh={onRefresh}
          showSettings
          settingsKey="context-inventory"
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          variant="atlas"
          mobileCardTemplate={mobileCardTemplate}
          mobileBreakpoint={600}
          className="context-inventory-organism__table"
        />
      )}
    </div>
  );
};

export default ContextInventoryOrganism;
